import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Autocomplete,
  CircularProgress,
  Chip,
  Box
} from '@mui/material';
import { AdviseSupplier, AdvisePricingModel } from '../../../types/advise';
import { Supplier } from '../../../types';
import { callExternalApi } from '../../../Api/callExternalApi';

interface SupplierFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (supplier: Partial<AdviseSupplier>) => Promise<AdviseSupplier | null>;
  supplier?: AdviseSupplier | null;
}

const PRICING_MODEL_OPTIONS = Object.values(AdvisePricingModel).map((v) => ({
  value: v,
  label: v.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}));

export const SupplierFormDialog = ({ open, onClose, onSave, supplier }: SupplierFormDialogProps) => {
  const isEdit = !!supplier;
  const [saving, setSaving] = useState(false);
  const [sfSupplierSearch, setSfSupplierSearch] = useState('');
  const [sfSuppliers, setSfSuppliers] = useState<Supplier[]>([]);
  const [loadingSfSuppliers, setLoadingSfSuppliers] = useState(false);

  const [form, setForm] = useState<Partial<AdviseSupplier>>({
    supplier_name: '',
    headquarters: '',
    pricing_model: '',
    product_category: '',
    capabilities: [],
    supported_features: [],
    strengths: [],
    limitations: [],
    channel_manager_name: '',
    channel_manager_email: '',
    channel_manager_phone: '',
    sf_supplier_id: undefined,
    sf_supplier_name: undefined
  });

  useEffect(() => {
    if (supplier) {
      setForm({ ...supplier });
    } else {
      setForm({
        supplier_name: '',
        headquarters: '',
        pricing_model: '',
        product_category: '',
        capabilities: [],
        supported_features: [],
        strengths: [],
        limitations: [],
        channel_manager_name: '',
        channel_manager_email: '',
        channel_manager_phone: '',
        sf_supplier_id: undefined,
        sf_supplier_name: undefined
      });
    }
  }, [supplier, open]);

  useEffect(() => {
    if (sfSupplierSearch.length < 2) return;
    const timer = setTimeout(async () => {
      setLoadingSfSuppliers(true);
      const { data } = await callExternalApi<Supplier[]>({
        url: `api/admin/suppliers?search=${encodeURIComponent(sfSupplierSearch)}`,
        method: 'GET',
        headers: { 'content-type': 'application/json' }
      });
      if (data) setSfSuppliers(data);
      setLoadingSfSuppliers(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [sfSupplierSearch]);

  const updateField = (field: string, value: unknown) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSfSupplierSelect = (sfSupplier: Supplier | null) => {
    if (sfSupplier) {
      updateField('sf_supplier_id', sfSupplier.sfId || sfSupplier.id);
      updateField('sf_supplier_name', sfSupplier.name);
      if (!form.supplier_name) updateField('supplier_name', sfSupplier.name);
    } else {
      updateField('sf_supplier_id', undefined);
      updateField('sf_supplier_name', undefined);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const result = await onSave(form);
      if (result) onClose();
    } finally {
      setSaving(false);
    }
  };

  const isValid = !!form.supplier_name?.trim();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth>
      <DialogTitle>{isEdit ? 'Edit Supplier' : 'Add Supplier'}</DialogTitle>
      <DialogContent>
        <Box className="flex flex-col gap-4 mt-2">
          <Autocomplete
            freeSolo
            options={sfSuppliers}
            getOptionLabel={(opt) => (typeof opt === 'string' ? opt : opt.name)}
            loading={loadingSfSuppliers}
            onInputChange={(_, val) => setSfSupplierSearch(val)}
            onChange={(_, val) => handleSfSupplierSelect(typeof val === 'string' ? null : val)}
            value={form.sf_supplier_name ? { name: form.sf_supplier_name, id: form.sf_supplier_id || '' } : null}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Link to Salesforce Supplier (optional)"
                size="small"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingSfSuppliers ? <CircularProgress size={16} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  )
                }}
              />
            )}
          />
          <TextField
            label="Supplier Name"
            required
            size="small"
            value={form.supplier_name || ''}
            onChange={(e) => updateField('supplier_name', e.target.value)}
          />
          <TextField
            label="Headquarters"
            size="small"
            value={form.headquarters || ''}
            onChange={(e) => updateField('headquarters', e.target.value)}
          />
          <Box className="flex gap-4">
            <FormControl
              size="small"
              className="flex-1">
              <InputLabel>Product Category</InputLabel>
              <Select
                label="Product Category"
                value={form.product_category || ''}
                onChange={(e) => updateField('product_category', e.target.value)}>
                <MenuItem value="CX">CX (CCaaS)</MenuItem>
                <MenuItem value="Network">Network (DIA/SD-WAN)</MenuItem>
                <MenuItem value="Security">Security</MenuItem>
                <MenuItem value="UCaaS">UCaaS</MenuItem>
                <MenuItem value="Colocation">Colocation</MenuItem>
              </Select>
            </FormControl>
            <FormControl
              size="small"
              className="flex-1">
              <InputLabel>Pricing Model</InputLabel>
              <Select
                label="Pricing Model"
                value={form.pricing_model || ''}
                onChange={(e) => updateField('pricing_model', e.target.value)}>
                {PRICING_MODEL_OPTIONS.map((opt) => (
                  <MenuItem
                    key={opt.value}
                    value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Autocomplete
            multiple
            freeSolo
            options={[]}
            value={form.strengths || []}
            onChange={(_, val) => updateField('strengths', val)}
            renderTags={(value, getTagProps) =>
              value.map((str, idx) => (
                <Chip
                  label={str}
                  size="small"
                  color="success"
                  variant="outlined"
                  {...getTagProps({ index: idx })}
                  key={str}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Strengths (press Enter)"
                size="small"
              />
            )}
          />
          <Autocomplete
            multiple
            freeSolo
            options={[]}
            value={form.limitations || []}
            onChange={(_, val) => updateField('limitations', val)}
            renderTags={(value, getTagProps) =>
              value.map((str, idx) => (
                <Chip
                  label={str}
                  size="small"
                  color="warning"
                  variant="outlined"
                  {...getTagProps({ index: idx })}
                  key={str}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Limitations (press Enter)"
                size="small"
              />
            )}
          />
          <Box className="border-t pt-3 mt-1">
            <Box className="text-sm font-medium text-gray-600 mb-2">Channel Manager</Box>
            <Box className="flex gap-4">
              <TextField
                label="Name"
                size="small"
                className="flex-1"
                value={form.channel_manager_name || ''}
                onChange={(e) => updateField('channel_manager_name', e.target.value)}
              />
              <TextField
                label="Email"
                size="small"
                className="flex-1"
                value={form.channel_manager_email || ''}
                onChange={(e) => updateField('channel_manager_email', e.target.value)}
              />
              <TextField
                label="Phone"
                size="small"
                className="flex-1"
                value={form.channel_manager_phone || ''}
                onChange={(e) => updateField('channel_manager_phone', e.target.value)}
              />
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!isValid || saving}>
          {saving ? <CircularProgress size={20} /> : isEdit ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
